/* eslint-disable max-lines-per-function */
import {
  GridCol,
  GridContainer,
  ModalManager,
  StickyBar,
  SvgIcon,
  useIsWindowBiggerThan,
} from '@bob/library'
import { MAIN_CONTENT_ID } from '@websrc/App/AppProvider'
import { CoralLogo } from '@websrc/App/Components/CoralLogo/CoralLogo'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapServicebar } from '@websrc/DataMappers/mapServicebar'
import { mapTopbar } from '@websrc/DataMappers/mapTopbar'
import { CartButton } from '@websrc/MainTopbar/CartButton/CartButton'
import { MainTopbarRegionModalProps } from '@websrc/MainTopbar/RegionModal/MainTopbarRegionModal'
import clsx from 'clsx'
import { visuallyHidden } from 'leds'
import React, { useLayoutEffect, useState } from 'react'

import { header, stickBar } from './MainTopbar.css'
import * as SC from './MainTopbar.style'
import { TopbarMenu } from './MenuList/TopbarMenu'
import { MainTopbarMobileModal } from './MobileModal/MainTopbarMobileModal'
import SearchModal from './SearchModal/SearchModal'

export interface MainTopbarProps {
  topbar?: ReturnType<typeof mapTopbar>
  servicebar?: ReturnType<typeof mapServicebar>
  markets: MainTopbarRegionModalProps
  noContainer?: boolean
  onlyShowLogo?: boolean
}

export default function MainTopbar({
  topbar,
  servicebar,
  markets,
  noContainer,
  onlyShowLogo,
}: MainTopbarProps) {
  const t = useStaticText()
  const isMobile = !useIsWindowBiggerThan(768)

  const [forceShowBurgerButton, setForceShowBurgerButton] = useState(false)
  const [moveCTAtoBurger, setMoveCTAtoBurger] = useState(false)
  const [moveRightMenuItemstoBurger, setMoveRightmenuItemstoBurger] = useState(false)
  const [primaryCTAsWidth, setPrimaryCTAsWidth] = useState(0)
  const [hideContent, setHideContent] = useState(true)
  const logoRef = React.useRef<HTMLDivElement>(null)
  const iconsRef = React.useRef<HTMLDivElement>(null)
  const navLeftRef = React.useRef<HTMLDivElement>(null)
  const navRightRef = React.useRef<HTMLDivElement>(null)
  const headerRef = React.useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const updateNavDisplay = () => {
      const logoWidth = logoRef.current?.offsetWidth || 0
      const iconsWidth = iconsRef.current?.offsetWidth || 0
      const navLeftWidth = navLeftRef.current?.offsetWidth || 0
      const navRightWidth = navRightRef.current?.offsetWidth || 0
      const headerWidth = headerRef.current?.offsetWidth || 0

      const isNavTooWide = logoWidth + iconsWidth + navLeftWidth + navRightWidth > headerWidth
      const isCTATooWide = logoWidth + iconsWidth + primaryCTAsWidth >= headerWidth
      const isRightMenuTooWide = logoWidth + navRightWidth + iconsWidth >= headerWidth

      if (isMobile) {
        setForceShowBurgerButton(true)
      } else {
        setForceShowBurgerButton(isNavTooWide)
      }
      setMoveCTAtoBurger(isCTATooWide)
      setMoveRightmenuItemstoBurger(isRightMenuTooWide)
      setHideContent(false)
    }

    window.addEventListener('resize', updateNavDisplay)
    updateNavDisplay()

    return () => {
      window.removeEventListener('resize', updateNavDisplay)
    }
  }, [isMobile, primaryCTAsWidth])

  // the first time we render and we're on mbile, we need to hide the content to prevent elements flickering in and out of view
  useLayoutEffect(() => {
    if (isMobile) {
      setHideContent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const serviceNav = (
    <nav className={header.serviceNav._}>
      {servicebar && (
        <>
          <TopbarMenu items={servicebar?.menu_items} />
          <SC.StyledAuthButton
            aria-label={'LEGO I D'}
            className={header.serviceNav.icons}
            legoId={servicebar.lego_id}
          />
        </>
      )}
      <SC.StyledRegionModalButton aria-label={t.header.language_selector} {...markets} />
    </nav>
  )

  return (
    <>
      {/* skip to content link for accessibility */}
      <a className={visuallyHidden} href={`#${MAIN_CONTENT_ID}`}>
        Skip navigation
      </a>

      {/* service navigation bar */}
      {!forceShowBurgerButton && !onlyShowLogo && !hideContent && (
        <div className={header.serviceNavWrapper}>
          {isMobile && (
            <GridContainer className={clsx(noContainer ? header.serviceNavNoContainer : '')}>
              <GridCol noGutter={noContainer}>{serviceNav}</GridCol>
            </GridContainer>
          )}
          {!isMobile && <div className={header.maxWidth}>{serviceNav}</div>}
        </div>
      )}

      {/* sticky topbar main navigation */}
      <StickyBar
        bgColor={'grayLight'}
        className={clsx(stickBar.zIndex)}
        noContainer={!isMobile ? true : noContainer}
      >
        <div
          className={clsx(noContainer ? header.noContainer : header.container, header.maxWidth)}
          ref={headerRef}
        >
          <div
            className={clsx(
              markets.currentMarket.isRtl ? header.logoRTL : header.logoLTR,
              hideContent ? header.nav.invisible : null
            )}
            ref={logoRef}
          >
            <CoralLogo />
          </div>

          {topbar && !onlyShowLogo && (
            <>
              <div
                aria-label={t.header.main_menu}
                className={clsx(
                  header.nav._,
                  forceShowBurgerButton ? header.nav.flex : null,
                  hideContent ? header.nav.invisible : null
                )}
              >
                <div
                  className={clsx(
                    markets.currentMarket.isRtl ? header.nav.right : header.nav.left,
                    forceShowBurgerButton ? header.nav.invisible : null
                  )}
                  ref={navLeftRef}
                >
                  <TopbarMenu items={topbar.left_menu_items} />
                </div>
                <div
                  className={clsx(markets.currentMarket.isRtl ? header.nav.left : header.nav.right)}
                  ref={navRightRef}
                >
                  <TopbarMenu
                    hideItems={moveRightMenuItemstoBurger}
                    hidePrimaryCTAs={moveCTAtoBurger}
                    items={topbar.menu_items}
                    primaryCTAsWidth={(width) => {
                      if (width > 0) setPrimaryCTAsWidth(width)
                    }}
                    showDropDownsOnRight={true}
                  />
                </div>
              </div>
            </>
          )}

          {topbar && servicebar && !onlyShowLogo && (
            <div
              className={clsx(
                markets.currentMarket.isRtl ? header.iconsNavRTL : header.iconsNavLTR
              )}
              ref={iconsRef}
            >
              {topbar.search && (
                <ModalManager key={'searchmodal'} modal={() => <SearchModal />} position={'center'}>
                  {(onClick) => (
                    <SC.ButtonTertiary
                      aria-label={t.search.search}
                      key={'searchbutton'}
                      onClick={onClick}
                    >
                      <SvgIcon name={'SEARCH'} size={24} />
                    </SC.ButtonTertiary>
                  )}
                </ModalManager>
              )}
              <CartButton aria-label={t.header.shopping_cart} shoppingCart={topbar.shopping_cart} />
              <MainTopbarMobileModal
                hideBottomBar={forceShowBurgerButton}
                hideCTA={moveCTAtoBurger}
                hideRightMenuItems={moveRightMenuItemstoBurger}
                leftMenuItems={topbar.left_menu_items}
                legoId={servicebar.lego_id}
                markets={markets}
                rightMenuItems={topbar.menu_items}
                servicebarItems={servicebar?.menu_items ?? []}
                showBurgerButton={forceShowBurgerButton}
              />
            </div>
          )}
        </div>
      </StickyBar>
    </>
  )
}
